import React, { useState, useEffect } from 'react'
import { Button, Card, Typography, theme, Space, Select } from 'antd'
import {
  SearchOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  CalendarOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import GoogleAddress from '../../reusable-components/GoogleAddress'
import SearchResults from './SearchResults'
import { geocodeByAddress } from 'react-google-places-autocomplete'
import { useSelector } from 'react-redux'

const { Title, Text } = Typography
const { Option } = Select

const ClinicSearchCriteria = ({
  onSearch,
  searchFilters = {},
  loading = false,
  onBack,
  onFilterChange,
  searchResults = [],
  error = null,
  totalResults = 0,
  onViewAvailability
}) => {
  const { token } = theme.useToken()
  const patientAddress = useSelector((state) => state.auth.agiliteUser.residentialAddress)

  // Step management
  const [currentStep, setCurrentStep] = useState(1)
  const [completedSteps, setCompletedSteps] = useState(new Set())

  // Form state
  const [address, setAddress] = useState(searchFilters.address || '')
  const [placeId, setPlaceId] = useState(searchFilters.placeId || '')
  const [location, setLocation] = useState(searchFilters.location || null)
  const [hasManuallySetLocation, setHasManuallySetLocation] = useState(false)
  const [appointmentReason, setAppointmentReason] = useState(searchFilters.appointmentReason || '')
  const [timeOfDay, setTimeOfDay] = useState(searchFilters.timeOfDay || '')

  // Animation state
  const [isAnimating, setIsAnimating] = useState(false)
  const [showResults, setShowResults] = useState(false)

  // Options
  const appointmentReasons = [
    { value: 'consultation', label: 'General Consultation' },
    { value: 'follow_up', label: 'Follow-up Visit' },
    { value: 'checkup', label: 'Health Check-up' },
    { value: 'prescription', label: 'Prescription Refill' },
    { value: 'test_results', label: 'Test Results Discussion' },
    { value: 'vaccination', label: 'Vaccination' },
    { value: 'chronic_care', label: 'Chronic Care Management' },
    { value: 'emergency', label: 'Urgent Care' },
    { value: 'other', label: 'Other' }
  ]

  const timeOfDayOptions = [
    { value: 'any', label: 'Any time' },
    { value: 'morning', label: 'Morning (6:00 - 12:00)' },
    { value: 'afternoon', label: 'Afternoon (12:00 - 18:00)' },
    { value: 'evening', label: 'Evening (18:00 - 22:00)' }
  ]

  // Auto-set patient address as default location
  useEffect(() => {
    console.log(
      '🏠 Address useEffect - patientAddress:',
      patientAddress,
      'hasManuallySetLocation:',
      hasManuallySetLocation,
      'address:',
      address
    )
    if (patientAddress && !hasManuallySetLocation && !address) {
      console.log('🏠 Auto-setting patient address:', patientAddress)
      setAddress(patientAddress)
      // Use setTimeout to ensure the address is set before geocoding
      setTimeout(() => {
        handleAddressChange(patientAddress, true)
      }, 200) // Increased timeout
    }
  }, [patientAddress, hasManuallySetLocation, address])

  // Handle field changes and step progression - NO AUTO SEARCH
  const handleFieldChange = (field, value) => {
    switch (field) {
      case 'appointmentReason':
        setAppointmentReason(value)
        if (value && !completedSteps.has(2)) {
          setCompletedSteps((prev) => new Set([...prev, 2]))
          setCurrentStep(3)
        }
        break
      case 'timeOfDay':
        setTimeOfDay(value)
        if (value && !completedSteps.has(3)) {
          setCompletedSteps((prev) => new Set([...prev, 3]))
        }
        // DO NOT AUTO-SEARCH - USER MUST CLICK BUTTON
        break
    }
  }

  const handleAddressChange = async (newAddress, isAutoSet = false) => {
    console.log('handleAddressChange called with:', newAddress, 'isAutoSet:', isAutoSet)

    // Always set the address, but only set hasManuallySetLocation for user changes
    setAddress(newAddress)
    if (!isAutoSet) {
      setHasManuallySetLocation(true)
    }

    if (!newAddress) {
      setPlaceId('')
      setLocation(null)
      // DO NOT call onFilterChange here - it might trigger auto-search
      // onFilterChange({
      //   ...searchFilters,
      //   address: '',
      //   placeId: '',
      //   location: null
      // })
      return
    }

    try {
      console.log('Geocoding address:', newAddress)
      const results = await geocodeByAddress(newAddress)
      if (results && results.length > 0) {
        const result = results[0]
        const newLocation = {
          lat: result.geometry.location.lat(),
          lng: result.geometry.location.lng()
        }
        const newPlaceId = result.place_id

        console.log('Geocoding successful:', { newLocation, newPlaceId })

        setLocation(newLocation)
        setPlaceId(newPlaceId)

        // Progress to next step when location is set - NO AUTO SEARCH
        console.log('Current completed steps:', completedSteps, 'Current step:', currentStep)
        if (newLocation && !completedSteps.has(1)) {
          console.log('Progressing to step 2')
          setCompletedSteps((prev) => new Set([...prev, 1]))
          setCurrentStep(2)
        }
        // DO NOT AUTO-SEARCH - USER MUST CLICK FIND DOCTORS BUTTON

        // DO NOT call onFilterChange here - it might trigger auto-search
        // onFilterChange({
        //   ...searchFilters,
        //   address: newAddress,
        //   placeId: newPlaceId,
        //   location: newLocation
        // })
      } else {
        console.log('No geocoding results found')
      }
    } catch (error) {
      console.error('Error geocoding address:', error)
    }
  }

  // Helper function to render steps conditionally
  const renderStep = (stepNumber) => {
    return currentStep >= stepNumber || completedSteps.has(stepNumber)
  }

  const canSearch = address && placeId && location && appointmentReason && timeOfDay

  // Handle search - NO ANIMATION, just call onSearch
  const handleSearch = () => {
    if (!canSearch) return

    console.log('🔍 ClinicSearchCriteria - User clicked Find Doctors, calling onSearch with:', {
      address,
      placeId,
      location,
      appointmentReason,
      timeOfDay
    })

    // Just call onSearch - let parent handle everything else
    onSearch({
      address,
      placeId,
      location,
      appointmentReason,
      timeOfDay
    })

    // DO NOT set any animation states or showResults
    // Let the parent component handle the transition
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative'
      }}
    >
      {/* Back button */}
      {onBack && (
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            background: 'rgba(255, 255, 255, 0.2)',
            border: 'none',
            color: 'white'
          }}
        >
          Back
        </Button>
      )}

      {/* Main search criteria card */}
      <Card
        style={{
          width: '100%',
          maxWidth: '600px',
          borderRadius: '20px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          border: 'none',
          opacity: isAnimating ? 0 : 1,
          transition: 'opacity 0.4s ease-out',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)'
        }}
        bodyStyle={{ padding: '40px' }}
      >
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <Title level={2} style={{ color: token.colorPrimary, marginBottom: '8px' }}>
            Find Your Doctor
          </Title>
          <Text style={{ fontSize: '16px', color: '#666' }}>
            Let's find the perfect healthcare professional for your needs
          </Text>
        </div>

        {/* Step 1: Location */}
        <div style={{ marginBottom: renderStep(2) ? '32px' : '48px' }}>
          <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
            <Space>
              <EnvironmentOutlined style={{ color: token.colorPrimary }} />
              Step 1: Your Location
            </Space>
          </Text>
          <GoogleAddress
            selectProps={{
              placeholder: 'Enter your address or location...',
              value: address ? { label: address, value: address } : null,
              onChange: (selectedOption) => {
                console.log('🏠 GoogleAddress onChange called with:', selectedOption)
                if (selectedOption) {
                  handleAddressChange(selectedOption.label, false)
                } else {
                  handleAddressChange('', false)
                }
              },
              styles: {
                input: (provided) => ({
                  ...provided,
                  color: 'black'
                }),
                option: (provided) => ({
                  ...provided,
                  color: 'black'
                })
              }
            }}
            style={{ width: '100%' }}
          />
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
            Debug: address="{address}", patientAddress="{patientAddress}"
          </div>
          {patientAddress && !hasManuallySetLocation && (
            <Text style={{ fontSize: '12px', color: '#52c41a', marginTop: '4px' }}>✓ Using your profile address</Text>
          )}
        </div>

        {/* Step 2: Appointment Reason */}
        {renderStep(2) && (
          <div
            style={{
              marginBottom: renderStep(3) ? '32px' : '48px',
              opacity: currentStep >= 2 ? 1 : 0.7,
              transform: currentStep >= 2 ? 'translateY(0)' : 'translateY(10px)',
              transition: 'all 0.3s ease'
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <CalendarOutlined style={{ color: token.colorPrimary }} />
                Step 2: Reason for Visit
              </Space>
            </Text>
            <Select
              placeholder='What brings you to the doctor today?'
              value={appointmentReason}
              onChange={(value) => handleFieldChange('appointmentReason', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={loading}
            >
              {appointmentReasons.map((reason) => (
                <Option key={reason.value} value={reason.value}>
                  {reason.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Step 3: Time of Day */}
        {renderStep(3) && (
          <div
            style={{
              marginBottom: '48px',
              opacity: currentStep >= 3 ? 1 : 0.7,
              transform: currentStep >= 3 ? 'translateY(0)' : 'translateY(10px)',
              transition: 'all 0.3s ease'
            }}
          >
            <Text strong style={{ display: 'block', marginBottom: '12px', color: '#374151', fontSize: '16px' }}>
              <Space>
                <ClockCircleOutlined style={{ color: token.colorPrimary }} />
                Step 3: Preferred Time
              </Space>
            </Text>
            <Select
              placeholder='When would you prefer your appointment?'
              value={timeOfDay}
              onChange={(value) => handleFieldChange('timeOfDay', value)}
              style={{ width: '100%' }}
              size='large'
              disabled={loading}
            >
              {timeOfDayOptions.map((time) => (
                <Option key={time.value} value={time.value}>
                  {time.label}
                </Option>
              ))}
            </Select>
          </div>
        )}

        {/* Search Button */}
        {canSearch && (
          <div style={{ textAlign: 'center' }}>
            <Button
              type='primary'
              size='large'
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
              style={{
                height: '50px',
                fontSize: '16px',
                fontWeight: '600',
                borderRadius: '25px',
                paddingLeft: '30px',
                paddingRight: '30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)'
              }}
            >
              Find Doctors
            </Button>
          </div>
        )}
      </Card>

      {/* DISABLED INTERNAL RESULTS RENDERING - Let parent handle this */}
      {/* Results section (fades in to take full screen) */}
      {/* COMMENTED OUT TO PREVENT AUTO-SHOWING RESULTS
      {(isAnimating || showResults) && (
        <div
          style={{
            position: 'absolute',
            left: '20px',
            right: '20px',
            top: '20px',
            bottom: '20px',
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)',
            opacity: showResults ? 1 : 0,
            transition: 'opacity 0.5s ease-in',
            transitionDelay: showResults ? '0.3s' : '0s',
            overflow: 'auto'
          }}
        >
          <div style={{ padding: '20px', height: '100%' }}>
            <SearchResults
              searchType={searchFilters.searchType || 'general_practitioner'}
              results={searchResults}
              loading={loading}
              onViewAvailability={onViewAvailability}
              error={error}
              totalResults={totalResults}
              searchFilters={searchFilters}
            />
          </div>
        </div>
      )}
      */}
    </div>
  )
}

export default ClinicSearchCriteria
